import type { ComponentProps } from 'react';

import { clsxm } from '@vs-shop/utils';

import { renderThrough, type RenderElement } from '../../util/render/render.tsx';

/**
 * A link inside a run of prose:
 *
 *   <TextLink render={<Link href={terms} />}>Obchodní podmínky</TextLink>
 *
 * Underlined and painted in the brand's attention colour, because a link inside
 * a paragraph has nothing but its own appearance to distinguish it from the
 * words around it — a colour alone would leave it invisible to a shopper who
 * cannot separate the two.
 *
 * It carries no size and no line height: a prose link reads at whatever size
 * the paragraph around it is set in, and taking one of its own would break the
 * line it sits on.
 *
 * The neutral element is an `<a>`; a tenant whose links go through a router
 * hands that element in through `render`.
 */
const textLinkClasses = 'text-destructive underline hover:opacity-75';

export type TextLinkProps = ComponentProps<'a'> & {
  /** The element to paint instead of the neutral `<a>` — a router's link. */
  readonly render?: RenderElement;
};

/** The prose link. */
export const TextLink = ({ className, render, ...props }: TextLinkProps) => {
  const classes = clsxm(textLinkClasses, className);

  return (
    renderThrough(render, 'text-link', classes, props) ?? (
      <a data-slot="text-link" className={classes} {...props} />
    )
  );
};
